import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { db } from "./firebase";
import { doc, getDoc } from "firebase/firestore";
import TutorLayout from "./TutorLayout";
import { BlockMath } from "react-katex";
import "katex/dist/katex.min.css";

const SelfMarkedReviewPage = () => {
  const { assignmentId, studentUID } = useParams();
  const [assignment, setAssignment] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [current, setCurrent] = useState(0);
  const [currentAttempt, setCurrentAttempt] = useState(0);
  const [submissionData, setSubmissionData] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);

  useEffect(() => {
    const allAnswers = submissionData?.answers?.filter(ans => ans.questionId === questions[current]?.id) || [];
    if (allAnswers.length > 0) {
      setCurrentAttempt(allAnswers.length - 1); // set to the LAST attempt
    } else {
      setCurrentAttempt(0); // no answers, just reset
    }
  }, [current, submissionData, questions]);
  

  useEffect(() => {
    const fetchData = async () => {
      const assignmentRef = doc(db, "assignments", assignmentId);
      const assignmentSnap = await getDoc(assignmentRef);

      const submissionRef = doc(db, "studentAssignmentSubmissions", `${studentUID}_${assignmentId}`);
      const submissionSnap = await getDoc(submissionRef);

      if (!assignmentSnap.exists() || !submissionSnap.exists()) {
        console.warn("Missing assignment or submission data");
        return;
      }

      const assignmentData = assignmentSnap.data();
      const submission = submissionSnap.data();

      setAssignment(assignmentData);
      setSubmissionData(submission);

      const questionDocs = await Promise.all(
        assignmentData.questions.map((qid) => getDoc(doc(db, "questions", qid)))
      );

      const fullQuestions = questionDocs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
      setQuestions(fullQuestions);
    };

    fetchData();
  }, [assignmentId, studentUID]);

  if (!assignment || questions.length === 0 || !submissionData) {
    return (
      <TutorLayout>
        <div style={{ padding: "2rem" }}>Loading...</div>
      </TutorLayout>
    );
  }

  const question = questions[current];
  const allAnswers = submissionData.answers?.filter(ans => ans.questionId === question.id) || [];

const latestAnswer = allAnswers.length > 0 ? allAnswers[currentAttempt] : null; 

  return (
    <TutorLayout>
      <div style={styles.container}>
        <h1 style={styles.heading}>🧑‍🏫 Reviewing: {assignment.title}</h1>

        {/* Progress Dots */}
        <div style={styles.progressWrapper}>
        {assignment.questions.map((_, index) => {
  let bgColor = "#636e72"; // default grey

  const allAnswersForQuestion = submissionData.answers
    ?.filter(ans => ans.questionId === questions[index].id) || [];

  const finalAttempt = allAnswersForQuestion.length > 0 
    ? allAnswersForQuestion[allAnswersForQuestion.length - 1] 
    : null;

  if (finalAttempt?.correct) {
    bgColor = "#00b894"; // green if final attempt is correct
  } else if (finalAttempt) {
    bgColor = "#d63031"; // red if final attempt exists but wrong
  }

  return (
    <div
      key={index}
      style={{
        ...styles.progressDot,
        backgroundColor: bgColor,
        border: index === current ? "3px solid #f9b710" : "2px solid transparent",
      }}
    >
      <span style={styles.dotNumber}>{index + 1}</span>
    </div>
  );
})}

        </div>

        {/* Question Card */}
        <div style={styles.card}>
          <h3>Question {current + 1} of {questions.length}</h3>

          {question.questionParagraphs ? (
            question.questionParagraphs.map((p, idx) => (
              typeof p === "string"
                ? <p key={idx}>{p}</p>
                : (
                  <div key={idx}>
                    {p.text && <p>{p.text}</p>}
                    {p.image && <img src={p.image} alt={`img-${idx}`} style={{ maxWidth: "100%", maxHeight: "300px" }} />}
                  </div>
                )
            ))
          ) : (
            <>
              {question.questionText && <p><strong>{question.questionText}</strong></p>}
              {question.questionLatex && <BlockMath math={question.questionLatex} />}
              {question.optionalQuestionText && <p>{question.optionalQuestionText}</p>}
            </>
          )}

          {/* Working */}
          <div style={{ marginTop: "1rem" }}>
            <label style={{ fontWeight: "bold" }}>Show your working</label>
            <textarea
              value={latestAnswer?.working || ""}
              readOnly
              style={styles.workingInput}
            />
          </div>

          {/* Final Answer */}
          {/* Final Answer */}
<div style={{ marginTop: "1rem" }}>
  <label style={{ fontWeight: "bold" }}>Answer</label>
  <div
    style={{
      padding: "0.5rem",
      backgroundColor: latestAnswer
        ? latestAnswer.correct
          ? "#dfffe0" // Light green
          : "#ffe0e0" // Light red
        : "#f5f5f5",   // Default grey if no answer
      borderRadius: "8px",
      transition: "background-color 0.3s ease",
    }}
  >
    {latestAnswer?.playerAnswer ? (
      <BlockMath math={latestAnswer.playerAnswer} />
    ) : (
      <p>—</p>
    )}
  </div>
</div>

          {latestAnswer && (
  <div style={{ marginTop: "1rem", fontSize: "1.2rem", fontWeight: "bold", textAlign: "center" }}>
    {latestAnswer.correct ? "✅ Correct" : "❌ Incorrect"}
  </div>
)}

{/* Reveal Answer Section */}
<div style={{ marginTop: "1rem" }}>
  {!showAnswer ? (
    <button
      onClick={() => setShowAnswer(true)}
      style={{
        padding: "0.5rem 1rem",
        backgroundColor: "#636e72",
        color: "#fff",
        border: "none",
        borderRadius: "6px",
        cursor: "pointer",
        fontWeight: "bold"
      }}
    >
      Reveal Answer
    </button>
  ) : (
    <>
      <div style={{ color: "#e74c3c", fontSize: "1.4rem" }}>
        <BlockMath math={String(question.answers?.[0] || question.answer || "")} />
      </div>
      <button
        onClick={() => setShowAnswer(false)}
        style={{
          marginTop: "0.5rem",
          padding: "0.4rem 0.9rem",
          backgroundColor: "#dfe6e9",
          color: "#2d3436",
          border: "1px solid #b2bec3",
          borderRadius: "6px",
          cursor: "pointer",
          fontWeight: "bold"
        }}
      >
        Hide Answer
      </button>
    </>
  )}
</div>

          {allAnswers.length > 1 && (
  <div style={{ marginTop: "1rem", textAlign: "center" }}>
    {/* Previous Attempt Button (only show if NOT on first) */}
    {currentAttempt > 0 && (
  <button
    onClick={() => setCurrentAttempt(i => i - 1)}
    style={{
      ...styles.backBtn,
      padding: "0.4rem 0.8rem",  // Smaller padding
      fontSize: "0.9rem",         // Smaller text
      minWidth: "100px",          // Smaller width if you want
    }}
  >
    ← Previous Attempt
  </button>
)}

{currentAttempt < allAnswers.length - 1 && (
  <button
    onClick={() => setCurrentAttempt(i => i + 1)}
    style={{
      ...styles.nextBtn,
      padding: "0.4rem 0.8rem",
      fontSize: "0.9rem",
      minWidth: "100px",
    }}
  >
    Next Attempt →
  </button>
)}

    {/* Attempt count */}
    <p style={{ marginTop: "0.5rem" }}>
      Attempt {currentAttempt + 1} of {allAnswers.length}
    </p>
  </div>
)}

         
        </div>

        {/* Nav Buttons */}
       {/* Nav Buttons */}
{/* Nav Buttons */}
<div style={{ display: "flex", justifyContent: "center", gap: "1rem", marginTop: "2rem" }}>
  {/* Previous button */}
  {current > 0 && (
    <button
      onClick={() => setCurrent(i => i - 1)}
      style={styles.backBtn}
    >
      ← Previous Question
    </button>
  )}

  {/* Next button */}
  {current < questions.length - 1 && (
    <button
      onClick={() => setCurrent(i => i + 1)}
      style={styles.nextBtn}
    >
      Next Question →
    </button>
  )}
</div>



      </div>
    </TutorLayout>
  );
};

const styles = {
  container: {
    padding: "2rem",
    fontFamily: "'Fredoka', cursive",
    backgroundColor: "#eaf4fc",
    minHeight: "100vh",
  },
  heading: {
    fontSize: "2rem",
    marginBottom: "1rem",
  },
  card: {
    backgroundColor: "#fff",
    padding: "2rem",
    borderRadius: "12px",
    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
    maxWidth: "700px",
    margin: "1rem auto 0",
  },
  workingInput: {
    width: "100%",
    borderRadius: "8px",
    border: "1px solid #ccc",
    padding: "0.75rem",
    fontSize: "1rem",
    fontFamily: "'Fredoka', cursive",
    marginTop: "1.25rem",
    backgroundColor: "#f5f5f5",
  },
  progressWrapper: {
    display: "flex",
    justifyContent: "center",
    gap: "12px",
    marginBottom: "1.5rem",
    flexWrap: "wrap",
  },
  progressDot: {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontWeight: "bold",
    fontSize: "1rem",
    color: "white",
    transition: "all 0.3s ease",
  },
  dotNumber: {
    userSelect: "none",
  },
  backBtn: {
    marginTop: "1rem",
    padding: "0.6rem 1.2rem",
    backgroundColor: "#00b894",
    color: "white",
    fontWeight: "bold",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    marginRight: "1rem",
  },
  nextBtn: {
    marginTop: "1rem",
    padding: "0.6rem 1.2rem",
    backgroundColor: "#00b894",
    color: "white",
    fontWeight: "bold",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
};

export default SelfMarkedReviewPage;
